import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search,
  Eye,
  MessageSquare,
  Filter,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Edit2,
  MoreHorizontal,
  UserPlus,
  CheckCircle,
  AlertCircle,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate, getStatusColor, getPriorityColor } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';

// SLA Status indicator component
function SLAStatusBadge({ ticket }: { ticket: any }) {
  const now = new Date();
  const slaDue = ticket.slaResolutionDue ? new Date(ticket.slaResolutionDue) : null;
  
  if (!slaDue || ticket.status === 'CLOSED' || ticket.status === 'RESOLVED') {
    return null;
  }

  const hoursRemaining = (slaDue.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  if (hoursRemaining < 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800">
        <XCircle className="w-3 h-3" />
        Breached
      </span>
    );
  } else if (hoursRemaining < 2) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
        <AlertTriangle className="w-3 h-3" />
        At Risk
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
        <CheckCircle2 className="w-3 h-3" />
        On Track
      </span>
    );
  }
}

// Urgent Unassigned Alert Banner
function UrgentUnassignedBanner({ onFilterClick }: { onFilterClick: () => void }) {
  const { data } = useQuery({
    queryKey: ['urgentUnassignedTickets'],
    queryFn: async () => {
      const response = await api.get('/tickets/urgent-unassigned');
      return response.data.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  if (!data || data.count === 0) return null;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-red-100 rounded-full">
          <AlertCircle className="w-5 h-5 text-red-600" />
        </div>
        <div>
          <p className="font-medium text-red-800">
            {data.count} Urgent/Critical ticket{data.count > 1 ? 's' : ''} unassigned
          </p>
          <p className="text-sm text-red-600">
            These tickets need immediate attention
          </p>
        </div>
      </div>
      <Button 
        variant="destructive" 
        size="sm"
        onClick={onFilterClick}
      >
        View & Assign
      </Button>
    </div>
  );
}

export default function TicketsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [priority, setPriority] = useState(searchParams.get('priority') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [assignee, setAssignee] = useState(searchParams.get('assignee') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [showFilters, setShowFilters] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  
  // Bulk selection state
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssigneeId, setBulkAssigneeId] = useState('');

  // Quick action mutations
  const closeTicketMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      const response = await api.post(`/tickets/${ticketId}/close`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      setActionMenuOpen(null);
    },
  });

  const assignToMeMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      // Get current user ID from the auth store to assign to self
      const userId = useAuthStore.getState().user?.id;
      if (!userId) throw new Error('User not authenticated');
      const response = await api.post(`/tickets/${ticketId}/assign`, { assigneeId: userId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['urgentUnassignedTickets'] });
      setActionMenuOpen(null);
    },
  });

  // Bulk assign mutation
  const bulkAssignMutation = useMutation({
    mutationFn: async ({ ticketIds, assigneeId }: { ticketIds: string[]; assigneeId: string }) => {
      const response = await api.post('/tickets/bulk-assign', { ticketIds, assigneeId });
      return response.data;
    },
    onSuccess: (data) => {
      const successCount = data.data?.results?.filter((r: any) => r.success).length || 0;
      toast({ 
        title: 'Success', 
        description: `Assigned ${successCount} ticket(s) to ${data.data?.assignee?.name || 'user'}` 
      });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['urgentUnassignedTickets'] });
      setSelectedTickets([]);
      setShowBulkAssignModal(false);
      setBulkAssigneeId('');
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.error || 'Failed to assign tickets', 
        variant: 'destructive' 
      });
    },
  });

  // Fetch ticket categories for filter
  const { data: categoriesData } = useQuery({
    queryKey: ['ticketCategoriesFilter'],
    queryFn: async () => {
      const response = await api.get('/tickets/public/categories');
      return response.data.data || [];
    },
  });

  // Fetch users for assignee filter (all IT staff, not just technicians)
  const { data: usersData } = useQuery({
    queryKey: ['itStaffUsers'],
    queryFn: async () => {
      const response = await api.get('/users?limit=100');
      return response.data.data || [];
    },
  });

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    if (category) params.set('category', category);
    if (assignee) params.set('assignee', assignee);
    setSearchParams(params);
  }, [page, search, status, priority, category, assignee, setSearchParams]);

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActionMenuOpen(null);
    if (actionMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [actionMenuOpen]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tickets', { page, search, status, priority, category, assignee }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (priority) params.append('priority', priority);
      if (category) params.append('categoryId', category);
      if (assignee) params.append('assigneeId', assignee);
      
      const response = await api.get(`/tickets?${params.toString()}`);
      return response.data;
    },
  });

  const tickets = data?.data || [];
  const pagination = data?.pagination;
  const categories = categoriesData || [];
  const users = usersData || [];

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPriority('');
    setCategory('');
    setAssignee('');
    setPage(1);
    setSelectedTickets([]);
  };

  const hasActiveFilters = search || status || priority || category || assignee;

  // Toggle ticket selection
  const toggleTicketSelection = (ticketId: string) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  // Select all visible tickets
  const toggleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map((t: any) => t.id));
    }
  };

  // Filter for urgent unassigned
  const showUrgentUnassigned = () => {
    setPriority('URGENT');
    setAssignee('unassigned');
    setStatus('OPEN');
    setShowFilters(true);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Urgent Unassigned Alert Banner */}
      <UrgentUnassignedBanner onFilterClick={showUrgentUnassigned} />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Tickets</h1>
          <p className="text-muted-foreground">
            Manage IT support tickets
            {pagination && ` • ${pagination.total} total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selectedTickets.length > 0 && (
            <Button 
              variant="secondary" 
              onClick={() => setShowBulkAssignModal(true)}
            >
              <Users className="w-4 h-4 mr-2" />
              Assign {selectedTickets.length} Selected
            </Button>
          )}
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <PermissionGate permission="tickets.create">
            <Button asChild>
              <Link to="/tickets/new">
                <Plus className="w-4 h-4 mr-2" />
                Create Ticket
              </Link>
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title, description, or ticket number..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[140px]"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PENDING">Pending</option>
                <option value="ON_HOLD">On Hold</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[140px]"
                value={priority}
                onChange={(e) => {
                  setPriority(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
                <option value="CRITICAL">Critical</option>
              </select>
              <Button 
                variant={showFilters ? "secondary" : "outline"} 
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
              </Button>
            </div>

            {/* Extended Filters Row */}
            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[180px]"
                  value={assignee}
                  onChange={(e) => {
                    setAssignee(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Assignees</option>
                  <option value="unassigned">Unassigned</option>
                  {users.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
                </select>
                {hasActiveFilters && (
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No tickets found</p>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters 
                  ? "Try adjusting your filters"
                  : "Create your first ticket to get started"}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <PermissionGate permission="tickets.create">
                  <Button asChild>
                    <Link to="/tickets/new">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Ticket
                    </Link>
                  </Button>
                </PermissionGate>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-4 w-10">
                      <input
                        type="checkbox"
                        checked={selectedTickets.length === tickets.length && tickets.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-input"
                      />
                    </th>
                    <th className="text-left p-4 font-medium">Ticket #</th>
                    <th className="text-left p-4 font-medium">Title</th>
                    <th className="text-left p-4 font-medium">Category</th>
                    <th className="text-left p-4 font-medium">Priority</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Requester</th>
                    <th className="text-left p-4 font-medium">Assigned To</th>
                    <th className="text-left p-4 font-medium">Created</th>
                    <th className="text-left p-4 font-medium">SLA Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tickets.map((ticket: any) => (
                    <tr 
                      key={ticket.id} 
                      className={cn(
                        "hover:bg-muted/50 transition-colors",
                        selectedTickets.includes(ticket.id) && "bg-primary/5"
                      )}
                    >
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedTickets.includes(ticket.id)}
                          onChange={() => toggleTicketSelection(ticket.id)}
                          className="rounded border-input"
                        />
                      </td>
                      <td className="p-4">
                        <Link 
                          to={`/tickets/${ticket.id}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {ticket.ticketNumber}
                        </Link>
                      </td>
                      <td className="p-4">
                        <div className="max-w-[250px]">
                          <Link 
                            to={`/tickets/${ticket.id}`}
                            className="font-medium hover:text-primary line-clamp-1"
                          >
                            {ticket.title || ticket.subject}
                          </Link>
                          {ticket.asset && (
                            <p className="text-xs text-muted-foreground">
                              Asset: {ticket.asset.assetTag}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center gap-1 text-sm">
                          {ticket.category?.icon && <span>{ticket.category.icon}</span>}
                          {ticket.category?.name || '-'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getPriorityColor(ticket.priority)
                        )}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getStatusColor(ticket.status)
                        )}>
                          {ticket.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4">
                        {ticket.requester ? (
                          <div>
                            <p className="font-medium text-sm">
                              {ticket.requester.firstName} {ticket.requester.lastName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {ticket.requester.email}
                            </p>
                          </div>
                        ) : ticket.employeeRequester ? (
                          <div>
                            <p className="font-medium text-sm">
                              {ticket.employeeRequester.firstName} {ticket.employeeRequester.lastName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {ticket.employeeRequester.email}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        {ticket.assignee ? (
                          <div>
                            <p className="font-medium text-sm">
                              {ticket.assignee.firstName} {ticket.assignee.lastName}
                            </p>
                          </div>
                        ) : (
                          <span className="text-orange-600 text-sm font-medium">Unassigned</span>
                        )}
                      </td>
                      <td className="p-4 text-sm">
                        {formatDate(ticket.createdAt)}
                      </td>
                      <td className="p-4">
                        <SLAStatusBadge ticket={ticket} />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild title="View Details">
                            <Link to={`/tickets/${ticket.id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                          <PermissionGate permission="tickets.update">
                            <Button variant="ghost" size="icon" asChild title="Edit Ticket">
                              <Link to={`/tickets/${ticket.id}/edit`}>
                                <Edit2 className="w-4 h-4" />
                              </Link>
                            </Button>
                          </PermissionGate>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            asChild 
                            className="relative" 
                            title="View Comments"
                          >
                            <Link to={`/tickets/${ticket.id}#comments`}>
                              <MessageSquare className="w-4 h-4" />
                              {ticket._count?.comments > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                                  {ticket._count.comments}
                                </span>
                              )}
                            </Link>
                          </Button>
                          {/* Quick Actions Menu */}
                          <div className="relative">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              title="More Actions"
                              onClick={() => setActionMenuOpen(actionMenuOpen === ticket.id ? null : ticket.id)}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                            {actionMenuOpen === ticket.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-background border rounded-md shadow-lg z-10">
                                {!ticket.assigneeId && (
                                  <button
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                                    onClick={() => assignToMeMutation.mutate(ticket.id)}
                                    disabled={assignToMeMutation.isPending}
                                  >
                                    <UserPlus className="w-4 h-4" />
                                    Assign to Me
                                  </button>
                                )}
                                {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && (
                                  <button
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                                    onClick={() => closeTicketMutation.mutate(ticket.id)}
                                    disabled={closeTicketMutation.isPending}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Close Ticket
                                  </button>
                                )}
                                <Link
                                  to={`/tickets/${ticket.id}`}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                                  onClick={() => setActionMenuOpen(null)}
                                >
                                  <Eye className="w-4 h-4" />
                                  View Details
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} tickets
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Bulk Assign Tickets</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Assign {selectedTickets.length} selected ticket{selectedTickets.length > 1 ? 's' : ''} to a team member
              </p>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mb-4"
                value={bulkAssigneeId}
                onChange={(e) => setBulkAssigneeId(e.target.value)}
              >
                <option value="">Select Assignee</option>
                {users.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowBulkAssignModal(false);
                    setBulkAssigneeId('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => bulkAssignMutation.mutate({ 
                    ticketIds: selectedTickets, 
                    assigneeId: bulkAssigneeId 
                  })}
                  disabled={!bulkAssigneeId || bulkAssignMutation.isPending}
                >
                  {bulkAssignMutation.isPending ? 'Assigning...' : 'Assign Tickets'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
